import React, { createContext, useContext, useState, useEffect, useLayoutEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';
import { API_URL } from '../config';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [theme, setTheme] = useState<Theme>(() => {
    // Tentar carregar do localStorage primeiro (fallback)
    const savedTheme = localStorage.getItem('theme') as Theme;
    return savedTheme || 'light';
  });

  // Aplicar tema ao documento sempre que o tema mudar (layout: evita flash claro no primeiro paint)
  useLayoutEffect(() => {
    localStorage.setItem('theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
    // Bootstrap 5.3 — ativa variáveis de modo escuro (formularios, input-group, links, etc.)
    document.documentElement.setAttribute('data-bs-theme', theme === 'dark' ? 'dark' : 'light');
    if (theme === 'dark') {
      document.body.classList.add('dark-theme');
      document.body.classList.remove('light-theme');
    } else {
      document.body.classList.add('light-theme');
      document.body.classList.remove('dark-theme');
    }
  }, [theme]);

  // Carregar tema do usuário quando ele fizer login
  useEffect(() => {
    if (isAuthenticated && user?.preferred_theme) {
      const userTheme = user.preferred_theme as Theme;
      if (userTheme !== theme) {
        setTheme(userTheme);
        localStorage.setItem('theme', userTheme);
      }
    } else if (!isAuthenticated) {
      // Se não estiver autenticado, usar tema do localStorage
      const savedTheme = localStorage.getItem('theme') as Theme;
      if (savedTheme && savedTheme !== theme) {
        setTheme(savedTheme);
      }
    }
  }, [isAuthenticated, user?.preferred_theme]);

  const toggleTheme = async () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    
    // Salvar no backend se o usuário estiver autenticado
    if (isAuthenticated && user) {
      try {
        await axios.put(`${API_URL}/api/auth/me/theme?theme=${newTheme}`);
        // Recarregar dados do usuário para atualizar o preferred_theme
        const userResponse = await axios.get(`${API_URL}/api/auth/me`);
        // O AuthContext não expõe setUser, então vamos apenas garantir que o localStorage está atualizado
        // O próximo login ou refresh da página carregará o tema correto
      } catch (error) {
        console.error('Erro ao salvar tema no backend:', error);
        // Continuar mesmo se falhar, pois já salvou no localStorage
      }
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};



